import { useEffect, Fragment } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  FileText,
  Banknote,
  CreditCard,
  ArrowDownCircle,
  ArrowUpCircle,
  TrendingUp,
  TrendingDown,
  Minus,
  Loader2,
  Download,
  Layers,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useCashRegisterReport } from "@/hooks/useCashRegisterReport";
import { useRestaurant } from "@/hooks/useRestaurant";
import { exportCashRegisterReportToPDF, exportCashRegisterReportToExcel } from "@/lib/report-export";
import { cn } from "@/lib/utils";

interface CashRegisterReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cashRegisterId: string | null;
}

export function CashRegisterReportDialog({
  open,
  onOpenChange,
  cashRegisterId,
}: CashRegisterReportDialogProps) {
  const { reportData, isLoading, fetchReport } = useCashRegisterReport();
  const { activeRestaurant } = useRestaurant();

  useEffect(() => {
    if (open && cashRegisterId) {
      fetchReport(cashRegisterId);
    }
  }, [open, cashRegisterId, fetchReport]);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);

  const formatDateTime = (dateString: string) =>
    format(new Date(dateString), "dd/MM/yyyy HH:mm", { locale: ptBR });

  const handleExportPDF = () => {
    if (!reportData) return;
    const exportData = {
      cashRegister: reportData.cashRegister,
      cashCount: reportData.cashCount,
      salesByPaymentMethod: reportData.salesByPaymentMethod,
      paymentMethodCounts: reportData.paymentMethodCounts.map(pc => ({
        paymentMethodName: reportData.salesByPaymentMethod.find(s => s.paymentMethodId === pc.paymentMethodId)?.paymentMethodName || "Desconhecido",
        countedAmount: pc.countedAmount,
      })),
      totals: reportData.totals,
    };
    exportCashRegisterReportToPDF(exportData, activeRestaurant?.name);
  };

  const handleExportExcel = () => {
    if (!reportData) return;
    const exportData = {
      cashRegister: reportData.cashRegister,
      cashCount: reportData.cashCount,
      salesByPaymentMethod: reportData.salesByPaymentMethod,
      paymentMethodCounts: reportData.paymentMethodCounts.map(pc => ({
        paymentMethodName: reportData.salesByPaymentMethod.find(s => s.paymentMethodId === pc.paymentMethodId)?.paymentMethodName || "Desconhecido",
        countedAmount: pc.countedAmount,
      })),
      totals: reportData.totals,
    };
    exportCashRegisterReportToExcel(exportData, activeRestaurant?.name);
  };

  

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Relatório de Fechamento de Caixa
            </DialogTitle>
            {reportData && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleExportPDF}>
                  <Download className="h-4 w-4 mr-1" />
                  PDF
                </Button>
                <Button variant="outline" size="sm" onClick={handleExportExcel}>
                  <Download className="h-4 w-4 mr-1" />
                  Excel
                </Button>
              </div>
            )}
          </div>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : !reportData ? (
          <div className="text-center py-12 text-muted-foreground">
            Não foi possível carregar o relatório.
          </div>
        ) : (
          <div className="space-y-6">
            {/* Header Info */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
              <div>
                <p className="text-sm text-muted-foreground">Abertura</p>
                <p className="font-medium">{formatDateTime(reportData.cashRegister.openedAt)}</p>
                {reportData.cashRegister.openedBy && (
                  <p className="text-xs text-muted-foreground">por {reportData.cashRegister.openedBy}</p>
                )}
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Fechamento</p>
                <p className="font-medium">
                  {reportData.cashRegister.closedAt
                    ? formatDateTime(reportData.cashRegister.closedAt)
                    : "Em aberto"}
                </p>
                {reportData.cashRegister.closedBy && (
                  <p className="text-xs text-muted-foreground">por {reportData.cashRegister.closedBy}</p>
                )}
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 border rounded-lg">
                <p className="text-xs text-muted-foreground">Fundo Inicial</p>
                <p className="text-lg font-bold">{formatCurrency(reportData.cashRegister.openingAmount)}</p>
              </div>
              <div className="p-3 border rounded-lg">
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <ArrowDownCircle className="h-3 w-3 text-green-600" />
                  Suprimentos
                </p>
                <p className="text-lg font-bold text-green-600">{formatCurrency(reportData.totals.supplies)}</p>
              </div>
              <div className="p-3 border rounded-lg">
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <ArrowUpCircle className="h-3 w-3 text-red-600" />
                  Sangrias
                </p>
                <p className="text-lg font-bold text-red-600">{formatCurrency(reportData.totals.withdrawals)}</p>
              </div>
              <div className="p-3 border rounded-lg">
                <p className="text-xs text-muted-foreground">Vendas Totais</p>
                <p className="text-lg font-bold text-primary">{formatCurrency(reportData.totals.totalSales)}</p>
              </div>
            </div>

            <Separator />

            {/* Cash Count Details */}
            {reportData.cashCount.length > 0 && (
              <div>
                <h4 className="font-medium flex items-center gap-2 mb-3">
                  <Banknote className="h-4 w-4" />
                  Contagem de Dinheiro
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {reportData.cashCount.map((item) => (
                    <div
                      key={item.denomination}
                      className="flex items-center justify-between p-2 border rounded text-sm"
                    >
                      <span>{formatCurrency(item.denomination)}</span>
                      <span className="text-muted-foreground">x{item.quantity}</span>
                      <span className="font-medium">{formatCurrency(item.total)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end mt-2">
                  <div className="p-2 bg-muted rounded-lg">
                    <span className="text-sm text-muted-foreground mr-2">Total em Dinheiro:</span>
                    <span className="font-bold">{formatCurrency(reportData.totals.totalCashCounted)}</span>
                  </div>
                </div>
              </div>
            )}

            <Separator />

            {/* Payment Method Comparison */}
            <div>
              <h4 className="font-medium flex items-center gap-2 mb-3">
                <CreditCard className="h-4 w-4" />
                Comparativo por Forma de Pagamento
              </h4>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 font-medium">Forma de Pagamento</th>
                      <th className="text-right p-3 font-medium">Vendido</th>
                      <th className="text-right p-3 font-medium">Contado</th>
                      <th className="text-right p-3 font-medium">Diferença</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Cash row - compare with cash count */}
                    {reportData.totals.totalCashSales > 0 || reportData.totals.totalCashCounted > 0 ? (
                      <tr className="border-t">
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Banknote className="h-4 w-4 text-muted-foreground" />
                            Dinheiro
                          </div>
                        </td>
                        <td className="text-right p-3">{formatCurrency(reportData.totals.totalCashSales)}</td>
                        <td className="text-right p-3">{formatCurrency(reportData.totals.totalCashCounted)}</td>
                        <td className="text-right p-3">
                          <DifferenceCell
                            expected={
                              reportData.cashRegister.openingAmount +
                              reportData.totals.supplies -
                              reportData.totals.withdrawals +
                              reportData.totals.totalCashSales
                            }
                            counted={reportData.totals.totalCashCounted}
                          />
                        </td>
                      </tr>
                    ) : null}

                    {/* Other payment methods */}
                    {reportData.salesByPaymentMethod
                      .filter((pm) => {
                        // Exclude cash, it's shown separately
                        const isCash = pm.paymentMethodName.toLowerCase().includes("dinheiro");
                        return !isCash;
                      })
                      .map((pm) => {
                        const counted = reportData.paymentMethodCounts.find(
                          (c) => c.paymentMethodId === pm.paymentMethodId
                        );
                        const countedAmount = counted?.countedAmount || 0;

                        return (
                          <tr key={pm.paymentMethodId} className="border-t">
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <CreditCard className="h-4 w-4 text-muted-foreground" />
                                {pm.paymentMethodName}
                              </div>
                            </td>
                            <td className="text-right p-3">{formatCurrency(pm.totalSales)}</td>
                            <td className="text-right p-3">
                              {countedAmount > 0 ? formatCurrency(countedAmount) : "-"}
                            </td>
                            <td className="text-right p-3">
                              {countedAmount > 0 ? (
                                <DifferenceCell expected={pm.totalSales} counted={countedAmount} />
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                  <tfoot className="bg-muted/30 font-medium">
                    <tr className="border-t">
                      <td className="p-3">Total Geral</td>
                      <td className="text-right p-3">{formatCurrency(reportData.totals.totalSales)}</td>
                      <td className="text-right p-3">
                        {formatCurrency(
                          reportData.totals.totalCashCounted + reportData.totals.totalPaymentMethodsCounted
                        )}
                      </td>
                      <td className="text-right p-3">
                        <DifferenceCell
                          expected={
                            reportData.cashRegister.openingAmount +
                            reportData.totals.supplies -
                            reportData.totals.withdrawals +
                            reportData.totals.totalSales
                          }
                          counted={
                            reportData.totals.totalCashCounted + reportData.totals.totalPaymentMethodsCounted
                          }
                        />
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <Separator />

            {/* Sales by Order Type */}
            <div>
              <h4 className="font-medium flex items-center gap-2 mb-3">
                <Layers className="h-4 w-4" />
                Vendas por Tipo de Pedido
              </h4>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 font-medium">Tipo</th>
                      <th className="text-right p-3 font-medium">Pedidos</th>
                      <th className="text-right p-3 font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.salesByOrderType.map((t) => (
                      <Fragment key={t.orderType}>
                        <tr className="border-t">
                          <td className="p-3 font-medium">{t.label}</td>
                          <td className="text-right p-3">
                            {t.orderCount > 0 ? t.orderCount : "—"}
                          </td>
                          <td className="text-right p-3">
                            {t.orderCount > 0 ? formatCurrency(t.totalSales) : "—"}
                          </td>
                        </tr>
                        {t.orderType === "delivery" &&
                          t.sources?.map((s) => (
                            <tr
                              key={`${t.orderType}-${s.source}`}
                              className="border-t bg-muted/20 text-muted-foreground"
                            >
                              <td className="p-3 pl-8">└ {s.label}</td>
                              <td className="text-right p-3">{s.orderCount}</td>
                              <td className="text-right p-3">{formatCurrency(s.totalSales)}</td>
                            </tr>
                          ))}
                      </Fragment>
                    ))}
                  </tbody>
                  <tfoot className="bg-muted/30 font-medium">
                    <tr className="border-t">
                      <td className="p-3">Total</td>
                      <td className="text-right p-3">
                        {reportData.salesByOrderType.reduce((s, t) => s + t.orderCount, 0)}
                      </td>
                      <td className="text-right p-3">
                        {formatCurrency(reportData.totals.totalGrossSales)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Inclui pedidos com status <em>paid</em> e <em>delivered</em>. Pedidos iFood com
                R$ 0,00 indicam sincronização pendente com a plataforma.
              </p>
            </div>

            {/* Notes */}
            {reportData.cashRegister.notes && (
              <>
                <Separator />
                <div>
                  <h4 className="font-medium mb-2">Observações</h4>
                  <p className="text-sm text-muted-foreground p-3 bg-muted/50 rounded-lg">
                    {reportData.cashRegister.notes}
                  </p>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DifferenceCell({ expected, counted }: { expected: number; counted: number }) {
  const diff = counted - expected;

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(Math.abs(value));

  if (Math.abs(diff) < 0.01) {
    return (
      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
        <Minus className="h-3 w-3 mr-1" />
        OK
      </Badge>
    );
  }

  if (diff > 0) {
    return (
      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
        <TrendingUp className="h-3 w-3 mr-1" />
        +{formatCurrency(diff)}
      </Badge>
    );
  }

  return (
    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
      <TrendingDown className="h-3 w-3 mr-1" />
      -{formatCurrency(diff)}
    </Badge>
  );
}
